import React, { useState } from 'react'
import { ChevronRight, Building2, Calendar, MapPin } from 'lucide-react'

const Experience = () => {
  const [activeIndex, setActiveIndex] = useState(0)
  
  const experiences = [
    {
      company: 'Quadcore Innovations',
      role: 'Co-Founder & Chief Technology Officer',
      period: 'Aug 2022 – Present',
      location: 'San Jose, CA',
      description: 'Leading technology strategy and development for Nuff.Cash, a cloud-native fintech platform, and building autonomous AI systems.',
      achievements: [
        'Architected and deployed production-grade, cloud-native fintech backend on $0/month infrastructure using Oracle Cloud Always Free VMs, TiDB Cloud Serverless, and optimized JVM settings',
        'Engineered dual-service architecture: Nuff.Cash API Server (Spring Boot 2.7.18) with 20 REST controllers, 27 services, 28 JPA repositories; paired with IronClad Ledger double-entry accounting system',
        'Built AI fraud detection achieving 35% reduction in fraudulent transactions using supervised learning models with 94% classification accuracy on 1M+ monthly transactions at sub-100ms latency',
        'Implemented production security stack: Firebase JWT/OAuth2, KYC verification, OFAC sanctions screening (7,394+ names), and AES-256 encryption via AWS Secrets Manager',
        'Built SoloForge: 4-agent CrewAI pipeline with Anthropic Claude Sonnet 4 for autonomous lead-to-revenue lifecycle targeting 29.8M US solopreneurs',
        'Led engineering team of 8 engineers establishing AI-centric SDLC with quality gates, model versioning, and compliance standards',
      ],
      tech: ['Spring Boot', 'Java 11/17', 'TiDB', 'Oracle Cloud', 'CrewAI', 'Anthropic Claude', 'Snowflake Cortex', 'React 18', 'TypeScript'],
    },
    {
      company: 'Apple',
      role: 'Solution Architect',
      period: 'Jun 2023 – Jul 2024',
      location: 'Cupertino, CA',
      description: 'Architected scalable AI/ML integration solutions for B2C systems.',
      achievements: [
        'Architected scalable AI/ML integration solutions improving data processing speed by 50% through optimized feature engineering and model serving infrastructure',
        'Implemented NLP pipelines (TF-IDF, Truncated SVD, NMF, PCA) using NLTK and scikit-learn for text analytics and sentiment analysis',
        'Designed AI-driven feedback loops boosting customer engagement by 22%',
        'Developed analytics dashboards with real-time model performance monitoring reducing incident resolution time by 60%',
        'Partnered with engineering leaders on enterprise-level AI standards and architectural patterns',
      ],
      tech: ['Python', 'TensorFlow', 'scikit-learn', 'NLTK', 'NLP', 'AWS SageMaker', 'Kubernetes'],
    },
    {
      company: 'JMT Worldwide',
      role: 'Co-Founder & Engineering Manager',
      period: 'Jul 2012 – Apr 2022',
      location: 'San Jose, CA',
      description: 'Built and led engineering team delivering enterprise CRM and microservices platforms.',
      achievements: [
        'Led team of 8+ full-stack engineers delivering enterprise CRM for 20K userbase',
        'Architected microservices platform with predictive analytics (Linear Regression, KNN) reducing latency 35%',
        'Improved customer retention by 25% through data-driven insights and ML-powered recommendations',
        'Established cloud-native AWS infrastructure and CI/CD pipelines via Kubernetes',
        'Integrated 3rd-party systems and payment platforms reducing costs 15% through automation',
      ],
      tech: ['AWS', 'Kubernetes', 'Python', 'React', 'Node.js', 'PostgreSQL', 'Docker', 'CI/CD'],
    },
    {
      company: 'Luminar Technologies',
      role: 'Technical Program Manager',
      period: 'Previous Role',
      location: 'Orlando, FL',
      description: 'Led cross-functional programs for autonomous driving AI systems.',
      achievements: [
        'Led cross-functional programs integrating field data into autonomous driving AI frameworks',
        'Reduced validation cycles by 30% through improved testing and data pipeline optimization',
        'Coordinated between hardware and software teams for LiDAR system integration',
        'Established data governance processes for autonomous vehicle training datasets',
      ],
      tech: ['Python', 'TensorFlow', 'LiDAR', 'Computer Vision', 'ROS', 'C++'],
    },
    {
      company: 'LinkedIn',
      role: 'Technical Program Manager',
      period: 'Previous Role',
      location: 'Sunnyvale, CA',
      description: 'Led automated data governance processes and enterprise compliance frameworks.',
      achievements: [
        'Led automated data governance processes via executive dashboards driving 60% efficiency gains',
        'Partnered with Enterprise Data Office on compliance frameworks',
        'Built cross-functional alignment between engineering, legal, and data teams',
        'Implemented data quality monitoring and alerting systems',
      ],
      tech: ['Data Governance', 'SQL', 'Tableau', 'Python', 'Spark', 'Hadoop'],
    },
    {
      company: 'Xtime',
      role: 'Technical Program Manager',
      period: 'Previous Role',
      location: 'San Jose, CA',
      description: 'Led B2B/B2C integration across automotive dealership SaaS platform.',
      achievements: [
        'Led B2B/B2C integration across 3,000+ auto dealers through SaaS platform',
        'Reduced system downtime by 20% through reliability improvements',
        'Drove system reliability and operational excellence initiatives',
        'Managed multi-stakeholder programs with OEM partners',
      ],
      tech: ['SaaS', 'REST APIs', 'Java', 'MySQL', 'AWS', 'Microservices'],
    },
  ]
  
  return (
    <section id="experience" className="py-24 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-display font-bold text-white mb-4">
            Professional <span className="text-gradient">Experience</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            A track record of building impactful AI systems and leading engineering teams at industry-leading companies.
          </p>
        </div>
        
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="space-y-2 lg:max-h-[600px] lg:overflow-y-auto lg:pr-2">
            {experiences.map((exp, index) => (
              <button
                key={index}
                onClick={() => setActiveIndex(index)}
                className={`w-full text-left p-4 rounded-xl transition-all ${
                  activeIndex === index
                    ? 'glass border-primary/50 bg-primary/5'
                    : 'hover:bg-white/5'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className={`font-semibold ${activeIndex === index ? 'text-primary' : 'text-white'}`}>
                      {exp.company}
                    </h4>
                    <p className="text-gray-400 text-sm">{exp.role}</p>
                  </div>
                  <ChevronRight className={`w-5 h-5 transition-transform ${activeIndex === index ? 'text-primary rotate-90' : 'text-gray-500'}`} />
                </div>
              </button>
            ))}
          </div>
          
          <div className="lg:col-span-2 glass rounded-2xl p-8">
            <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
              <div>
                <h3 className="text-2xl font-display font-bold text-white">
                  {experiences[activeIndex].role}
                </h3>
                <p className="text-primary font-semibold">{experiences[activeIndex].company}</p>
              </div>
              <div className="space-y-1 text-right">
                <div className="flex items-center gap-2 text-gray-400 text-sm">
                  <Calendar size={16} />
                  {experiences[activeIndex].period}
                </div>
                <div className="flex items-center gap-2 text-gray-400 text-sm">
                  <MapPin size={16} />
                  {experiences[activeIndex].location}
                </div>
              </div>
            </div>
            
            <p className="text-gray-300 mb-6">{experiences[activeIndex].description}</p>
            
            <div className="space-y-3 mb-6 max-h-64 overflow-y-auto pr-2">
              {experiences[activeIndex].achievements.map((achievement, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0" />
                  <p className="text-gray-300 text-sm">{achievement}</p>
                </div>
              ))}
            </div>
            
            <div className="flex flex-wrap gap-2">
              {experiences[activeIndex].tech.map((tech, i) => (
                <span key={i} className="px-3 py-1 rounded-full bg-white/5 text-primary text-sm border border-primary/20">
                  {tech}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Experience
